import { useCallback, useEffect, useRef, useState } from 'react';
import type { IncidentBlock, ProviderListResponse, StateResponse } from '../lib/api.js';
import { fetchProviders } from '../lib/api.js';
import {
  type DashboardResource,
  createDashboardResource,
  markResourceError,
  markResourceLoading,
  markResourceSuccess,
  updateResourceStaleness,
} from '../lib/dashboardResource.js';
import { useDashboard } from '../lib/store.js';
import { useStateStream } from './useStateStream.js';

interface UseDashboardStateOptions {
  statePollIntervalMs?: number;
  providersPollIntervalMs?: number;
  staleAfterMs?: number;
}

interface UseDashboardStateResult {
  state: StateResponse | null;
  providers: ProviderListResponse | null;
  incident: IncidentBlock | null;
  stateResource: DashboardResource<StateResponse>;
  providersResource: DashboardResource<ProviderListResponse>;
  incidentResource: DashboardResource<IncidentBlock | null>;
  refreshState: () => Promise<void>;
  refreshProviders: () => Promise<void>;
}

export function useDashboardState({
  statePollIntervalMs = 60_000,
  providersPollIntervalMs = 60_000,
  staleAfterMs = 90_000,
}: UseDashboardStateOptions = {}): UseDashboardStateResult {
  const { state, providers, incident, setState, setProviders, setIncident } = useDashboard();

  const {
    state: stateResource,
    incident: incidentResource,
    refreshState,
  } = useStateStream(setState, setIncident, { pollIntervalMs: statePollIntervalMs, staleAfterMs });
  const mountedRef = useRef(true);

  const [providersResource, setProvidersResource] = useState(
    createDashboardResource<ProviderListResponse>(),
  );

  const refreshProviders = useCallback(async () => {
    if (!mountedRef.current) {
      return;
    }

    setProvidersResource((resource) => markResourceLoading(resource));
    try {
      const payload = await fetchProviders();
      if (!mountedRef.current) {
        return;
      }
      setProviders(payload);
      setProvidersResource((resource) =>
        markResourceSuccess(resource, payload, new Date().toISOString()),
      );
    } catch (error) {
      if (!mountedRef.current) {
        return;
      }
      setProvidersResource((resource) => markResourceError(resource, error));
    }
  }, [setProviders]);

  useEffect(() => {
    mountedRef.current = true;
    refreshProviders();
    const providersPoller = setInterval(() => {
      refreshProviders();
    }, providersPollIntervalMs);
    return () => {
      mountedRef.current = false;
      clearInterval(providersPoller);
    };
  }, [providersPollIntervalMs, refreshProviders]);

  useEffect(() => {
    const interval = setInterval(() => {
      setProvidersResource((resource) => updateResourceStaleness(resource, staleAfterMs));
    }, 1_000);
    return () => clearInterval(interval);
  }, [staleAfterMs]);

  return {
    state,
    providers,
    incident,
    stateResource,
    providersResource,
    incidentResource,
    refreshState,
    refreshProviders,
  };
}
